import React, { useState } from "react";
import { Mail, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "", enquiry_type: "general" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill all required fields");
      return;
    }
    setSending(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Message sent. We'll reply within 1 business day.");
    } catch {
      toast.error("Something went wrong. Try again or email hello@switchtt.co.uk");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-28 md:pt-36 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="font-mono text-[10px] text-[#3B82F6] uppercase tracking-[0.3em] mb-6">/ Contact</div>
        <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter text-white leading-[0.92] max-w-4xl">
          Let's build<br />something brilliant<span className="text-[#3B82F6]">.</span>
        </h1>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-0 border border-white/10">
          {/* LEFT INFO */}
          <div className="lg:col-span-5 p-10 md:p-14 bg-[#0a0a0a] border-b lg:border-b-0 lg:border-r border-white/10 space-y-10">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#3B82F6] mb-3">Email</div>
              <a href="mailto:hello@switchtt.co.uk" className="text-white hover:text-[#3B82F6] font-display text-2xl tracking-tight inline-flex items-center gap-3">
                <Mail size={18} /> hello@switchtt.co.uk
              </a>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#3B82F6] mb-3">Office</div>
              <div className="text-white/80 text-lg font-body leading-relaxed inline-flex items-start gap-3">
                <MapPin size={18} className="mt-1 shrink-0" />
                <span>4 Queens Rd<br />London SW19 8YE<br />United Kingdom</span>
              </div>
            </div>
            <div className="pt-10 border-t border-white/10">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">Coverage</div>
              <p className="text-white/60 text-sm font-body leading-relaxed">United Kingdom · Europe · United States · Remote.</p>
            </div>
          </div>

          {/* RIGHT FORM */}
          <div className="lg:col-span-7 p-10 md:p-14">
            {!sent ? (
              <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-white/60 font-mono mb-2 block">I'm enquiring as a *</Label>
                  <Select value={form.enquiry_type} onValueChange={onChange("enquiry_type")}>
                    <SelectTrigger data-testid="select-enquiry-type" className="bg-white/5 border-white/10 rounded-none h-12 text-white focus:ring-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10 text-white rounded-none">
                      <SelectItem value="general">General enquiry</SelectItem>
                      <SelectItem value="hire">Employer, I want to hire</SelectItem>
                      <SelectItem value="candidate">Candidate, I'm looking for my next role</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-white/60 font-mono mb-2 block">Your name *</Label>
                    <Input required value={form.name} onChange={onChange("name")} data-testid="contact-name" className="bg-white/5 border-white/10 rounded-none h-12 text-white focus-visible:ring-0 focus-visible:border-[#3B82F6]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-white/60 font-mono mb-2 block">Email *</Label>
                    <Input required type="email" value={form.email} onChange={onChange("email")} data-testid="contact-email" className="bg-white/5 border-white/10 rounded-none h-12 text-white focus-visible:ring-0 focus-visible:border-[#3B82F6]" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-white/60 font-mono mb-2 block">Company</Label>
                    <Input value={form.company} onChange={onChange("company")} data-testid="contact-company" className="bg-white/5 border-white/10 rounded-none h-12 text-white focus-visible:ring-0 focus-visible:border-[#3B82F6]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-white/60 font-mono mb-2 block">Subject *</Label>
                    <Input required value={form.subject} onChange={onChange("subject")} data-testid="contact-subject" className="bg-white/5 border-white/10 rounded-none h-12 text-white focus-visible:ring-0 focus-visible:border-[#3B82F6]" />
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-white/60 font-mono mb-2 block">How can we help? *</Label>
                  <Textarea required rows={6} value={form.message} onChange={onChange("message")} data-testid="contact-message" className="bg-white/5 border-white/10 rounded-none text-white focus-visible:ring-0 focus-visible:border-[#3B82F6] resize-none" />
                </div>

                <button type="submit" disabled={sending} data-testid="submit-contact" className="btn-volt px-8 py-4 text-xs uppercase tracking-[0.2em] disabled:opacity-60">
                  {sending ? "Sending…" : "Send message →"}
                </button>
              </form>
            ) : (
              <div className="py-16 text-center" data-testid="contact-success">
                <CheckCircle2 size={48} className="text-[#3B82F6] mx-auto mb-6" />
                <h3 className="font-display text-3xl text-white mb-3 tracking-tight">Message sent</h3>
                <p className="text-white/60 font-body">Thanks, we'll be in touch within 1 business day.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
